import { useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  X,
  CaretRight,
  SquaresFour,
  Info,
  CreditCard,
  Gift,
  GraduationCap,
  EnvelopeSimple,
  House,
  SignOut,
} from "@phosphor-icons/react";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import usePostData from "Hooks/usePostData";
import GetDrawerLinks from "Content/drawer";
import GetMobileMenuLinks from "Content/mobileMenu";
import Logo from "./Components/Logo";
import AuthButtons from "./Components/AuthButtons";
import LanguageSwitcher from "Components/Button/LanguageSwitcher";
import Spinner from "Components/RequestHandler/Spinner";

// Path → icon map. Fallback to House if unmatched.
const ICON_FOR_PATH = {
  "/categories": SquaresFour,
  "/about": Info,
  "/plans": CreditCard,
  "/gift": Gift,
  "/my-courses": GraduationCap,
  "/contact": EnvelopeSimple,
};

const MobileDrawer = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoggedIn, allowedCourses, selectedUser, logoutHandler, token } =
    useContext(LoginAuthContext);
  const navigate = useNavigate();
  const { postData, isLoading } = usePostData();
  const isRTL = i18n.language === "ar";

  const hasFullNav =
    isAuthenticated || (isLoggedIn && allowedCourses?.length > 0);
  const drawerLinks = GetDrawerLinks();
  const mobileMenuLinks = GetMobileMenuLinks(isAuthenticated);
  const links = hasFullNav ? mobileMenuLinks : drawerLinks;

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleLinkClick = (path) => {
    onClose();
    setTimeout(() => navigate(path), 300);
  };

  const logout = async () => {
    await postData("logout", {}, token);
    logoutHandler();
    onClose();
    navigate("/");
  };

  return (
    <div
      className={`fixed inset-0 z-[10000] transition-all duration-300 ${
        isOpen ? "visible" : "invisible"
      }`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`absolute top-0 end-0 w-full sm:w-[360px] h-[100dvh] flex flex-col transition-transform duration-300
          bg-gradient-to-b from-[#0f0e0f] via-black to-black
          border-s border-white/[0.08]
          ${isOpen ? "translate-x-0" : "ltr:translate-x-full rtl:-translate-x-full"}`}
      >
        {/* Subtle primary glow — top-end corner decoration */}
        <div
          aria-hidden
          className="absolute -top-24 -end-24 w-60 h-60 rounded-full bg-primary/20 blur-3xl pointer-events-none"
        />

        {/* Header */}
        <div className="relative sticky top-0 flex items-center justify-between text-white py-4 px-5 border-b border-white/[0.08] z-10">
          <Logo className="w-24" onClick={onClose} />
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 transition-colors p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close menu"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="relative flex-1 overflow-y-auto">
          {/* Profile card — only when authenticated */}
          {hasFullNav && selectedUser && (
            <div className="px-5 pt-5">
              <button
                onClick={() => {
                  onClose();
                  setTimeout(() => navigate("/my-profiles"), 300);
                }}
                className="w-full flex items-center gap-3 bg-[#1f1f22] border border-white/[0.08] rounded-2xl p-4 text-start hover:border-white/[0.16] transition-colors"
              >
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-darkPrimary flex items-center justify-center text-white font-extrabold text-lg ring-2 ring-primary/30">
                    {selectedUser?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-base truncate">
                    {selectedUser?.name || t("navigation.profile")}
                  </p>
                  <p className="text-gray-400 text-xs mt-0.5">
                    {t("navigation.manage_profile") || "Manage profile"}
                  </p>
                </div>
                <CaretRight
                  size={16}
                  className={`text-gray-500 flex-shrink-0 ${isRTL ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          )}

          {/* Nav links */}
          <nav className="flex flex-col text-white py-4 px-5 gap-1.5">
            {links.map((link, index) => {
              const Icon = ICON_FOR_PATH[link.path] || House;
              return (
                <button
                  key={index}
                  onClick={() => handleLinkClick(link.path)}
                  className="group flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-white/[0.05] active:bg-white/[0.08] transition-colors text-start"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 group-hover:border-primary/30 transition-colors">
                    <Icon size={18} weight="duotone" className="text-white/80 group-hover:text-primary transition-colors" />
                  </div>
                  <span className="flex-1 capitalize text-base font-medium text-white/90 group-hover:text-white transition-colors">
                    {link.name}
                  </span>
                  <CaretRight
                    size={14}
                    className={`text-gray-600 group-hover:text-primary transition-colors ${isRTL ? "rotate-180" : ""}`}
                  />
                </button>
              );
            })}
          </nav>

          {/* Separator between nav and auth action */}
          <div className="mx-5 border-t border-white/[0.08]" />

          {/* Auth section */}
          <div className="px-5 pt-5 pb-6">
            {hasFullNav ? (
              <button
                onClick={logout}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-primary/10 border border-primary/30 text-primary font-semibold hover:bg-primary/15 transition-colors min-h-[48px] disabled:opacity-60"
              >
                {isLoading ? (
                  <Spinner isSmall />
                ) : (
                  <>
                    <SignOut size={18} weight="bold" />
                    <span>{t("navigation.logout")}</span>
                  </>
                )}
              </button>
            ) : (
              <div className="flex justify-center">
                <AuthButtons onNavigate={onClose} />
              </div>
            )}
          </div>
        </div>

        {/* Language switcher footer */}
        <div className="relative py-4 px-5 border-t border-white/[0.08] flex justify-center">
          <LanguageSwitcher isDrawer />
        </div>
      </div>
    </div>
  );
};

export default MobileDrawer;
